// ─────────────────────────────────────────────────────────────────────
// Round DI-Country — Digital Inspection session-country pin.
//
// Legacy MDIParent1 kept a CountryCode GLOBAL set by the clicked
// USA/India button; every Pluto/Mafia call for the rest of the session
// used it. The modern workspace components (InspectionSection, the
// dynamic-box hooks, AttachmentsPanel, AuditLog, ...) call ~30
// InspectionController endpoints whose DTOs do NOT carry a country, so
// without this pin those calls fell back to the caller's PROFILE
// country — a cross-country session could start against one database
// and save against the other.
//
// This module is that global, made explicit and validated:
//   • setDICountry(c)  — pin the SERVER-CONFIRMED effective country
//                        ("USA"/"INDIA", from load-job-details /
//                        start-inspection-context responses).
//   • clearDICountry() — unpin (DI page unmount).
//   • getDICountry()   — read the pin.
//
// A request interceptor on the SHARED axios instance attaches the pin
// as the X-DI-Country header — but ONLY for Digital Inspection URLs
// (di/, Inspection/, DIAttachment/), so no other module's requests are
// touched and no shared file (api.js) needed modification. The server
// validates the header (only "USA"/"INDIA" honored — see
// DIRequestHelper.EffectiveCountry) and maps it to a FIXED
// configuration key; the header can never name a connection string.
// An explicit DTO countrycode still wins over the header server-side.
// ─────────────────────────────────────────────────────────────────────
import api from "../../api";

export const DI_COUNTRY_HEADER = "X-DI-Country";

let activeDICountry = "";

/** Pin the active DI session country ("USA" / "INDIA"; "" clears). */
export function setDICountry(country) {
  const c = String(country || "").trim().toUpperCase();
  activeDICountry = c === "USA" || c === "INDIA" ? c : "";
}

export function clearDICountry() {
  activeDICountry = "";
}

export function getDICountry() {
  return activeDICountry;
}

/** Human label for UI ("USA Database" / "India Database"). */
export function diCountryLabel(country) {
  return String(country || "").trim().toUpperCase() === "INDIA"
    ? "India Database"
    : "USA Database";
}

// URL match: baseURL is ".../api" and DI calls are made with relative
// paths like "di/...", "/Inspection/...", "/DIAttachment/..." (both
// leading-slash and bare forms appear in the DI sources).
const DI_URL_RE = /^\/?(di|inspection|diattachment)\b/i;

let interceptorRegistered = false;
function registerInterceptor() {
  if (interceptorRegistered) return;
  interceptorRegistered = true;
  api.interceptors.request.use((config) => {
    try {
      if (activeDICountry && DI_URL_RE.test(String(config?.url || ""))) {
        config.headers = config.headers || {};
        if (typeof config.headers.set === "function") {
          config.headers.set(DI_COUNTRY_HEADER, activeDICountry);
        } else {
          config.headers[DI_COUNTRY_HEADER] = activeDICountry;
        }
      }
    } catch {
      /* never break a request over the pin */
    }
    return config;
  });
}
registerInterceptor();
